import { Menu, Plus } from "lucide-react";
import type { JSX } from "react";
import { Button } from "@mailflare/ui";

export interface TopbarProps {
  readonly onOpenMenu: () => void;
  readonly onOpenShortcuts: () => void;
  readonly onCompose: () => void;
  readonly canCompose: boolean;
  readonly children: JSX.Element;
  readonly trailing?: JSX.Element;
}

/**
 * Application header. Sits on the tinted canvas with no divider so the search
 * pill and the Compose button read as the primary surfaces (Gmail-like).
 */
export function Topbar({
  onOpenMenu,
  onOpenShortcuts,
  onCompose,
  canCompose,
  children,
  trailing,
}: TopbarProps): JSX.Element {
  return (
    <header className="flex flex-wrap items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label="Open navigation"
        onClick={onOpenMenu}
      >
        <Menu aria-hidden className="size-5" />
      </Button>

      <span className="flex items-center md:hidden" aria-hidden>
        <span className="bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-lg text-sm font-bold">
          M
        </span>
      </span>

      <div className="order-last w-full min-w-0 basis-full lg:order-none lg:w-auto lg:flex-1">
        {children}
      </div>

      <Button
        variant="ghost"
        size="icon"
        aria-label="Keyboard shortcuts"
        onClick={onOpenShortcuts}
        className="hidden lg:inline-flex"
      >
        <span aria-hidden className="font-mono text-sm">
          ?
        </span>
      </Button>

      <Button
        onClick={onCompose}
        disabled={!canCompose}
        data-testid="compose-button"
        variant="secondary"
        size="lg"
        className="gap-2"
      >
        <Plus aria-hidden className="size-5" />
        <span className="hidden sm:inline">Compose</span>
        <span className="sr-only sm:hidden">Compose</span>
      </Button>

      {trailing}
    </header>
  );
}
